import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface RetreatCarouselProps {
  title: string;
  images: string[];
  autoPlayInterval?: number;
}
export function RetreatCarousel({
  title,
  images,
  autoPlayInterval = 5000
}: RetreatCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const hasMultiple = images.length > 1;
  const goTo = useCallback((idx: number) => {
    setCurrent((idx % images.length + images.length) % images.length);
    setResetKey(k => k + 1);
  }, [images.length]);
  useEffect(() => {
    if (!hasMultiple) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length);
    }, autoPlayInterval);
    return () => clearInterval(timer);
  }, [resetKey, hasMultiple, images.length, autoPlayInterval]);
  return <div className="flex flex-col" data-id="element-45" data-mp="3ak9qap2skexc">
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-black border border-white/10" data-id="element-46" data-mp="mkjhy328a4na">
        <AnimatePresence mode="wait" initial={false} data-id="element-47" data-mp="hkx3jqanyicw">
          <motion.div key={current} initial={{
          opacity: 0,
          scale: 1.03
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeInOut'
        }} className="absolute inset-0" data-id="element-48" data-mp="hn9toqqkrz11">
            <img src={images[current]} alt={`${title} retreat`} className="w-full h-full object-cover" data-id="element-49" data-mp="3q074gv2thhj3" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-50" data-mp="3l49zfkx07xlm" />
          </motion.div>
        </AnimatePresence>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-6 text-center z-10" data-id="element-51" data-mp="1j8g21hwvji39">
          <span className="font-cinzel text-sm md:text-base lg:text-lg text-gold tracking-[0.2em] uppercase" data-id="element-52" data-mp="3dp69l5q0i3ar">
            {title}
          </span>
        </div>

        {/* Arrows — only when multiple images */}
        {hasMultiple && <>
            <button onClick={() => goTo(current - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-white/20 bg-black/50 backdrop-blur-sm text-cream/60 hover:text-gold hover:border-gold/50 transition-all duration-300" aria-label={`Previous ${title} image`} data-id="element-53" data-mp="38r4v6y3j6s5m">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-54" data-mp="3rhw2lu3yk8hd" />
            </button>
            <button onClick={() => goTo(current + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-white/20 bg-black/50 backdrop-blur-sm text-cream/60 hover:text-gold hover:border-gold/50 transition-all duration-300" aria-label={`Next ${title} image`} data-id="element-55" data-mp="3rk0cqtsga3q3">
              <ChevronRightIcon className="w-4 h-4" data-id="element-56" data-mp="23mhd20w5667k" />
            </button>
          </>}
      </div>

      {/* Dots — only when multiple images */}
      {hasMultiple && <div className="flex justify-center gap-2 mt-3" data-id="element-57" data-mp="2dj3x4ngn8t8j">
          {images.map((_, idx) => <button key={idx} onClick={() => goTo(idx)} className={`w-2 h-2 rounded-full transition-all duration-400 ${idx === current ? 'bg-gold scale-125' : 'bg-white/20 hover:bg-white/40'}`} aria-label={`Go to ${title} image ${idx + 1}`} data-id="element-58" data-mp="1pthzi4fx6m2n" data-mpi={idx} />)}
        </div>}
    </div>;
}